import { type RefObject } from 'react';

import { createHeightSync } from './createHeightSync';

const ribbonHeightSync = createHeightSync();

/**
 * Coordinates the top ribbon strip height across the sibling offer cards. A card renders a ribbon
 * (cashback or time-of-use) flush to its top edge, pushing its body down; a card without one starts
 * its body at the top. Each card reports the natural height of its ribbon region and the provider
 * tracks the tallest, so cards without a ribbon can reserve that height as an empty spacer — lining
 * up where every card's body ("the card element") starts, rather than where the ribbon starts.
 */
export const RibbonHeightSyncProvider = ribbonHeightSync.Provider;

/**
 * Measures the natural height of a card's ribbon region and syncs it with the sibling cards via the
 * {@link RibbonHeightSyncProvider}. Returns `ribbonRef` for the (unconstrained) ribbon content to
 * measure and `ribbonWrapperRef` for the element that reserves the shared max height. A card with
 * no visible ribbon measures 0 (so it never inflates the max) yet still reserves the spacer.
 */
export const useRibbonHeightSync = (): {
  ribbonRef: RefObject<HTMLDivElement | null>;
  ribbonWrapperRef: RefObject<HTMLDivElement | null>;
} => {
  const { measureRef, reserveRef } = ribbonHeightSync.useHeightSync();
  return { ribbonRef: measureRef, ribbonWrapperRef: reserveRef };
};
